import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import { buttonVariants } from '../Button/variants';
import {
  getActiveFullscreenTable,
  subscribeToFullscreenTable,
} from '../Table/fullscreen';
import type {
  SelectContentProps,
  SelectGroupProps,
  SelectItemProps,
  SelectLabelProps,
  SelectProps,
  SelectRootProps,
  SelectScrollDownButtonProps,
  SelectScrollUpButtonProps,
  SelectSeparatorProps,
  SelectTriggerActionProps,
  SelectTriggerProps,
  SelectValueProps,
} from './types';

/**
 * A clickable action for use inside a `customTriggers` render(), e.g. wrapped
 * in a `Tooltip`. The trigger's own root is a native `<button>`, so this
 * intentionally renders a `<span role="button">` rather than `Button` —
 * nesting a real `<button>` inside another `<button>` is invalid HTML.
 */
const SelectTriggerAction = React.forwardRef<
  HTMLSpanElement,
  SelectTriggerActionProps
>(
  (
    { className, size = 'icon', disabled, onClick, onKeyDown, ...props },
    ref,
  ) => {
    return (
      <span
        ref={ref}
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={(event) => {
          if (disabled) return;
          onClick?.(event);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.(event);
          }
          onKeyDown?.(event);
        }}
        className={cn(
          buttonVariants({ variant: 'ghost', size }),
          disabled &&
            'eye:pointer-events-none eye:cursor-not-allowed eye:bg-grey-disabled eye:text-grey-disabled',
          className,
        )}
        {...props}
      />
    );
  },
);

SelectTriggerAction.displayName = 'SelectTriggerAction';

function SelectRoot({ ...props }: SelectRootProps) {
  return <SelectPrimitive.Root data-slot='select' {...props} />;
}

function SelectGroup({ ...props }: SelectGroupProps) {
  return <SelectPrimitive.Group data-slot='select-group' {...props} />;
}

function SelectValue({ className, ...props }: SelectValueProps) {
  return (
    <span
      data-slot='select-value'
      className={cn(
        'eye:block eye:min-w-0 eye:flex-1 eye:truncate eye:text-left',
        className,
      )}
    >
      <SelectPrimitive.Value {...props} />
    </span>
  );
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    { className, children, indicatorClassName, customTriggers, ...props },
    ref,
  ) => {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        data-slot='select-trigger'
        className={cn(
          'eye:group/select-trigger eye:body-m eye:flex eye:h-48 eye:w-full eye:min-w-0 eye:cursor-pointer eye:items-center eye:justify-between eye:gap-8 eye:rounded-md eye:border eye:px-16',
          'eye:border-grey-100 eye:bg-grey-5 eye:text-black',
          'eye:outline-offset-3 eye:outline-black eye:transition-[border-color,outline-color,box-shadow]',
          'eye:hover:border-black',
          'eye:focus:border-black eye:focus:outline-2 eye:focus:outline-black',
          'eye:disabled:cursor-not-allowed eye:disabled:border-grey-100 eye:disabled:bg-grey-disabled eye:disabled:text-grey-disabled',
          'eye:aria-invalid:border-red-error eye:aria-invalid:hover:border-red-error eye:aria-invalid:focus:border-red-error',
          'eye:disabled:data-placeholder:text-grey-placeholder-disabled eye:data-placeholder:text-grey-placeholder',
          'eye:[&_svg]:pointer-events-none eye:[&_svg]:shrink-0',
          className,
        )}
        {...props}
      >
        {children}
        {customTriggers?.map((action) => (
          // Sibling of the value, before the chevron. Stop propagation so
          // interacting with the action does not open the select.
          <span
            key={action.key}
            className='eye:flex eye:shrink-0 eye:items-center'
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {action.render()}
          </span>
        ))}
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon
            data-slot='select-trigger-icon'
            className={cn(
              'eye:pointer-events-none eye:size-20 eye:shrink-0 eye:text-black eye:transition-transform eye:duration-150 eye:group-data-[state=open]/select-trigger:rotate-180',
              indicatorClassName,
            )}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  },
);

SelectTrigger.displayName = 'SelectTrigger';

function SelectContent({
  align = 'start',
  children,
  className,
  slotProps,
  position = 'popper',
  side = 'bottom',
  sideOffset = 6,
  ...props
}: SelectContentProps) {
  const activeFullscreenTable = React.useSyncExternalStore(
    subscribeToFullscreenTable,
    getActiveFullscreenTable,
    () => null,
  );
  const portalContainer =
    slotProps?.portal?.container ?? activeFullscreenTable ?? undefined;

  return (
    <SelectPrimitive.Portal {...slotProps?.portal} container={portalContainer}>
      <SelectPrimitive.Content
        data-slot='select-content'
        position={position}
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'eye:relative eye:z-1410 eye:box-border eye:w-(--radix-select-trigger-width) eye:min-w-(--radix-select-trigger-width) eye:overflow-hidden',
          'eye:rounded-md eye:border eye:border-grey-40 eye:bg-white eye:text-black',
          'eye:shadow-popover-content',
          'eye:duration-100 eye:data-[state=closed]:animate-out eye:data-[state=closed]:fade-out-0 eye:data-[state=closed]:zoom-out-95',
          'eye:data-[state=open]:animate-in eye:data-[state=open]:fade-in-0 eye:data-[state=open]:zoom-in-95',
          'eye:data-[side=bottom]:slide-in-from-top-2 eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2 eye:data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          data-slot='select-viewport'
          className={cn(
            'eye:max-h-[324px] eye:w-full eye:scroll-py-4 eye:overflow-y-auto eye:py-4xs',
            slotProps?.viewport?.className,
          )}
          {...slotProps?.viewport}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

const SelectGroupLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  SelectLabelProps
>(({ className, ...props }, ref) => {
  return (
    <SelectPrimitive.Label
      ref={ref}
      data-slot='select-label'
      className={cn(
        'eye:px-16 eye:py-[6px] eye:text-12 eye:text-grey-120!',
        className,
      )}
      {...props}
    />
  );
});

SelectGroupLabel.displayName = 'SelectGroupLabel';

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, ...props }, ref) => {
  return (
    <SelectPrimitive.Item
      ref={ref}
      data-slot='select-item'
      className={cn(
        'eye:relative eye:flex eye:min-h-[44px] eye:w-full eye:items-center eye:border-0 eye:px-16 eye:py-[6px]',
        'eye:text-14 eye:text-black eye:transition-[background-color,color] eye:duration-150',
        'eye:cursor-pointer eye:outline-none eye:select-none eye:focus:outline-none eye:focus-visible:outline-none',
        'eye:hover:bg-grey-10 eye:hover:text-black',
        'eye:data-highlighted:bg-grey-10 eye:data-highlighted:text-black',
        // Shape, not colour alone: the tinted background is far below 3:1.
        'eye:data-highlighted:inset-ring-2 eye:data-highlighted:inset-ring-black',
        'eye:data-[state=checked]:bg-grey-20 eye:data-[state=checked]:text-black eye:data-[state=checked]:hover:bg-grey-40',
        'eye:[&[data-state=checked][data-highlighted]]:bg-grey-20 eye:[&[data-state=checked][data-highlighted]]:text-black eye:[&[data-state=checked][data-highlighted]:hover]:bg-grey-40',
        'eye:data-disabled:pointer-events-none eye:data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className='eye:block eye:w-full eye:whitespace-normal'>
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        data-slot='select-item-indicator'
        className='eye:ml-auto eye:flex eye:shrink-0 eye:items-center'
      >
        <CheckIcon aria-hidden='true' className='eye:size-16' />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});

SelectItem.displayName = 'SelectItem';

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  SelectSeparatorProps
>(({ className, ...props }, ref) => {
  return (
    <SelectPrimitive.Separator
      ref={ref}
      data-slot='select-separator'
      className={cn('eye:my-4 eye:h-px eye:bg-grey-20', className)}
      {...props}
    />
  );
});

SelectSeparator.displayName = 'SelectSeparator';

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  SelectScrollUpButtonProps
>(({ className, ...props }, ref) => {
  return (
    <SelectPrimitive.ScrollUpButton
      ref={ref}
      data-slot='select-scroll-up-button'
      className={cn(
        'eye:flex eye:h-32 eye:items-center eye:justify-center eye:bg-white eye:text-grey-disabled',
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className='eye:size-20' />
    </SelectPrimitive.ScrollUpButton>
  );
});

SelectScrollUpButton.displayName = 'SelectScrollUpButton';

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  SelectScrollDownButtonProps
>(({ className, ...props }, ref) => {
  return (
    <SelectPrimitive.ScrollDownButton
      ref={ref}
      data-slot='select-scroll-down-button'
      className={cn(
        'eye:flex eye:h-32 eye:items-center eye:justify-center eye:bg-white eye:text-grey-disabled',
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className='eye:size-20' />
    </SelectPrimitive.ScrollDownButton>
  );
});

SelectScrollDownButton.displayName = 'SelectScrollDownButton';

const Select = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectProps
>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-invalid': ariaInvalid,
      children,
      disabled,
      id,
      label,
      placeholder,
      required,
      slotProps,
      ...rootProps
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const shouldRenderLabel = label != null;
    const triggerId = id ?? (shouldRenderLabel ? generatedId : undefined);
    const labelId =
      shouldRenderLabel && triggerId ? `${triggerId}-label` : undefined;
    const { className: labelClassName, ...restLabelProps } =
      slotProps?.label ?? {};
    const {
      className: triggerClassName,
      indicatorClassName,
      ...restTriggerProps
    } = slotProps?.trigger ?? {};

    const trigger = (
      <SelectTrigger
        ref={ref}
        id={triggerId}
        aria-label={ariaLabel}
        aria-labelledby={
          ariaLabelledBy ?? (!ariaLabel && labelId ? labelId : undefined)
        }
        aria-invalid={ariaInvalid}
        disabled={disabled}
        className={triggerClassName}
        indicatorClassName={indicatorClassName}
        {...restTriggerProps}
      >
        <SelectValue placeholder={placeholder} {...slotProps?.value} />
      </SelectTrigger>
    );

    return (
      <SelectRoot disabled={disabled} required={required} {...rootProps}>
        {shouldRenderLabel ? (
          <div
            data-slot='select-with-label'
            className='eye:group/select-with-label eye:relative eye:block eye:w-full'
          >
            <Label
              id={labelId}
              htmlFor={triggerId}
              className={cn(
                'eye:body-s-highlight',
                'eye:absolute eye:top-1 eye:left-12 eye:z-10 eye:m-0 eye:block eye:max-w-[calc(100%-32px)] eye:-translate-y-1/2 eye:truncate eye:bg-label-gradient eye:px-[3px] eye:leading-none eye:whitespace-nowrap',
                'eye:group-has-disabled/select-with-label:text-grey-disabled',
                'eye:group-has-[[data-slot=select-trigger][aria-invalid=true]]/select-with-label:text-red-100',
                labelClassName,
              )}
              {...restLabelProps}
            >
              {required ? `${label} *` : label}
            </Label>
            {trigger}
          </div>
        ) : (
          trigger
        )}
        <SelectContent
          {...slotProps?.content}
          slotProps={{
            portal: slotProps?.portal,
            viewport: slotProps?.viewport,
          }}
        >
          {children}
        </SelectContent>
      </SelectRoot>
    );
  },
);

Select.displayName = 'Select';

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectGroupLabel,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectTriggerAction,
  SelectValue,
};
